import React, { useMemo } from "react";
import { Formik, Form } from "formik";
import { useQueryClient } from "@tanstack/react-query";

import TenantForm from "components/TenantForm/TenantForm";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import Loader from "shared/Icons/Loader/Loader";
import { PrimaryButton } from "widgets";
import {
  useEditTenantDetails,
  useFetchGradeList,
  useFetchVerticalList,
} from "store/backend/apiHooks";
import {
  buildTenantUpdatePayload,
  createTenantSchema,
  mapGradesToOptions,
  mapVerticalsToOptions,
  resolveTenantUuid,
  useTenantDetailsLookups,
} from "helpers/tenantUtils";
import tenantPageStyles from "containers/TenantDetails/TenantDetails.module.scss";

import styles from "./EditTenantDetails.module.scss";

const EditTenantDetails = ({ tenantDetails, tenantId, onViewDetails }) => {
  const { openSnackbar } = useSnackbarContext();
  const queryClient = useQueryClient();
  const { mutate: editTenantDetails, isPending: isEditTenantDetailsLoading } =
    useEditTenantDetails();

  const {
    countryOptions,
    initialValues,
    isFormReady,
    isCountryListLoading,
    isErrorOnCountryList,
    errorMessageOnFetchCountryList,
  } = useTenantDetailsLookups(tenantDetails, { includeIds: true });

  const {
    data: verticalList,
    isPending: isVerticalListLoading,
    isError: isErrorOnVerticalList,
    error: errorMessageOnFetchVerticalList,
  } = useFetchVerticalList();

  const {
    data: gradeList,
    isPending: isGradeListLoading,
    isError: isErrorOnGradeList,
    error: errorMessageOnFetchGradeList,
  } = useFetchGradeList();

  const verticalOptions = verticalList?.items
    ? mapVerticalsToOptions(verticalList.items)
    : [];
  const gradeOptions = gradeList?.items
    ? mapGradesToOptions(gradeList.items)
    : [];
  const tenantSchema = useMemo(
    () => createTenantSchema(countryOptions),
    [countryOptions],
  );

  if (!tenantDetails || !tenantId) return null;

  if (!isFormReady || !initialValues) {
    return (
      <div
        className={tenantPageStyles.loadingWrap}
        role="status"
        aria-live="polite"
      >
        <Loader inline />
      </div>
    );
  }

  return (
    <div className={styles.EditTenantDetailsContainer}>
      <div className={styles.tenantDetailsContent}>
        <Formik
          key={tenantId}
          initialValues={initialValues}
          validationSchema={tenantSchema}
          validateOnMount={false}
          validateOnChange
          validateOnBlur
          onSubmit={(values, { setSubmitting }) => {
            const payload = buildTenantUpdatePayload(
              values,
              countryOptions,
              resolveTenantUuid(tenantDetails, tenantId),
            );
            editTenantDetails(payload, {
              onSuccess: () => {
                setSubmitting(false);
                queryClient.invalidateQueries({
                  queryKey: ["tenantDetails", tenantId],
                });
                openSnackbar("Tenant details updated successfully!", "success");
                onViewDetails?.();
              },
              onError: (error) => {
                setSubmitting(false);
                openSnackbar(
                  `Error updating tenant details: ${error.message}`,
                  "error",
                );
              },
            });
          }}
        >
          {({
            values,
            errors,
            touched,
            dirty,
            setFieldValue,
            setFieldTouched,
            handleBlur,
            handleSubmit,
            isSubmitting,
            submitCount,
          }) => (
            <Form onSubmit={handleSubmit}>
              <TenantForm
                values={values}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                handleBlur={handleBlur}
                touched={touched}
                dirty={dirty}
                errors={errors}
                submitCount={submitCount}
                verticalOptions={verticalOptions}
                gradeOptions={gradeOptions}
                countryOptions={countryOptions}
                isVerticalOrGradeListLoading={
                  isVerticalListLoading || isGradeListLoading
                }
                isCountryListLoading={isCountryListLoading}
                isErrorOnVerticalList={isErrorOnVerticalList}
                isErrorOnGradeList={isErrorOnGradeList}
                isErrorOnCountryList={isErrorOnCountryList}
                errorMessageOnFetchVerticalList={
                  errorMessageOnFetchVerticalList
                }
                errorMessageOnFetchGradeList={errorMessageOnFetchGradeList}
                errorMessageOnFetchCountryList={
                  errorMessageOnFetchCountryList
                }
                disableHasSingleBranchEdit
              />
              <div className={styles.submitRow}>
                <PrimaryButton
                  type="submit"
                  disabled={isSubmitting || isEditTenantDetailsLoading}
                >
                  {isEditTenantDetailsLoading || isSubmitting ? (
                    <Loader inline />
                  ) : (
                    "Submit"
                  )}
                </PrimaryButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default EditTenantDetails;
